// Recruitment screen - auction-based gladiator hiring

import { Canvas } from '../Canvas';
import { CONSTANTS, ScreenState } from '../types';
import { Screen, ScreenContext } from './Screen';
import { GameState, getCurrentTeam } from '../systems/GameState';
import { GAME_CONSTANTS } from '../data/constants';
import { GladiatorData } from '../entities/Gladiator';
import { hasRace } from '../entities/Team';

export class RecruitScreen implements Screen {
  readonly state = ScreenState.RECRUIT;
  private context: ScreenContext;
  private gameState: GameState | null = null;
  private selectedGladiatorIndex: number = 0;
  private bidAmount: number = 50;
  private bidInputText: string = '50';
  private bidInputFocused: boolean = false;
  private recruitmentDone: boolean = false;

  constructor(context: ScreenContext) {
    this.context = context;
  }

  setGameState(gameState: GameState): void {
    this.gameState = gameState;
  }

  enter(): void {
    console.log('Entering RecruitScreen');
    this.selectedGladiatorIndex = 0;
    this.bidAmount = 50;
    this.bidInputText = '50';
    this.bidInputFocused = false;

    // Check if recruitment already completed this month
    if (this.gameState && this.gameState.currentRecruitRound > GAME_CONSTANTS.RECRUIT_TURNS) {
      this.recruitmentDone = true;
    } else {
      this.recruitmentDone = false;
      // Process any AI turns until it's a player's turn
      this.processAITurns();
    }
  }

  exit(): void {
    console.log('Exiting RecruitScreen');
  }

  update(_deltaTime: number): void {}

  render(canvas: Canvas): void {
    if (!this.gameState) {
      canvas.clear(CONSTANTS.RECRUIT_BACKGROUND);
      canvas.drawText('Loading...', 400, 260, CONSTANTS.BIG_FONT, '#000000', 'center');
      return;
    }

    const team = getCurrentTeam(this.gameState);
    if (!team) {
      canvas.clear(CONSTANTS.RECRUIT_BACKGROUND);
      canvas.drawText('No team selected', 400, 260, CONSTANTS.BIG_FONT, '#000000', 'center');
      return;
    }

    // Background
    canvas.clear(CONSTANTS.RECRUIT_BACKGROUND);

    // Header
    this.renderHeader(canvas, team);

    // Recruitable gladiators list
    this.renderRecruitList(canvas);

    // Selected gladiator details
    this.renderSelectedGladiator(canvas);

    // Bid controls
    this.renderBidControls(canvas, team);

    // Back button
    this.renderBackButton(canvas);
  }

  private renderHeader(canvas: Canvas, team: any): void {
    canvas.drawRect(0, 0, CONSTANTS.CANVAS_WIDTH, 50, CONSTANTS.TOP1_BACKGROUND);

    canvas.drawText('Värväys', 20, 12, CONSTANTS.BIG_FONT, '#000000');
    const roundText = this.recruitmentDone
      ? 'Värväys päättynyt'
      : `Kierros ${this.gameState!.currentRecruitRound}/${GAME_CONSTANTS.RECRUIT_TURNS}`;
    canvas.drawText(roundText, 400, 12, CONSTANTS.BIG_FONT, '#000000', 'center');
    canvas.drawText(
      `${GAME_CONSTANTS.MONEY_PREFIX}: ${team.money} ${GAME_CONSTANTS.MONEY_SUFFIX}`,
      CONSTANTS.CANVAS_WIDTH - 20,
      12,
      CONSTANTS.BIG_FONT,
      '#000000',
      'right'
    );
  }

  private renderRecruitList(canvas: Canvas): void {
    const listX = 20;
    const listY = 60;
    const listWidth = 300;
    const listHeight = 400;

    canvas.drawRect(listX, listY, listWidth, listHeight, '#ffffff');
    canvas.drawRectOutline(listX, listY, listWidth, listHeight, CONSTANTS.TABLE_BORDER);

    canvas.drawText('Värväyttävät gladiaattorit', listX + 10, listY + 5, CONSTANTS.MAIN_FONT, '#000000');

    const team = getCurrentTeam(this.gameState!);
    const itemHeight = 45;
    const startY = listY + 30;

    for (let i = 0; i < this.gameState!.recruitableGladiatorIds.length; i++) {
      const gladiatorId = this.gameState!.recruitableGladiatorIds[i];
      const gladiator = this.gameState!.gladiators.get(gladiatorId);
      if (!gladiator) continue;

      const itemY = startY + i * itemHeight;

      // Selection highlight
      if (i === this.selectedGladiatorIndex) {
        canvas.drawRect(listX + 2, itemY, listWidth - 4, itemHeight - 2, CONSTANTS.SELECTED_GLADIATOR);
      }

      // Portrait
      const portrait = this.context.assets.gladiators[gladiator.race.id];
      if (portrait) {
        canvas.drawImage(portrait, listX + 5, itemY + 5, 36, 36);
      }

      // Name and race
      canvas.drawText(gladiator.name, listX + 50, itemY + 5, CONSTANTS.MAIN_FONT, CONSTANTS.GLADIATOR_TITLE);
      canvas.drawText(gladiator.race.name, listX + 50, itemY + 22, CONSTANTS.MSG_FONT, '#666666');

      // Current bid
      if (gladiator.offerTeamId) {
        const bidTeam = this.gameState!.teams.get(gladiator.offerTeamId);
        const bidText = `${gladiator.offerPrice} kr - ${bidTeam?.name ?? 'Unknown'}`;
        canvas.drawText(bidText, listX + listWidth - 10, itemY + 12, CONSTANTS.MSG_FONT, '#006600', 'right');
      }

      // Check if team already has this race
      if (team && hasRace(team, gladiator.race, this.gameState!.gladiators)) {
        canvas.drawText('(Rotu jo joukkueessa)', listX + 50, itemY + 32, CONSTANTS.MSG_FONT, '#cc0000');
      }

      // Click region
      canvas.addClickableRegion({
        id: `recruit-${i}`,
        x: listX,
        y: itemY,
        width: listWidth,
        height: itemHeight,
        onClick: () => {
          this.selectedGladiatorIndex = i;
        },
      });
    }
  }

  private renderSelectedGladiator(canvas: Canvas): void {
    const detailX = 340;
    const detailY = 60;
    const detailWidth = 440;
    const detailHeight = 300;

    canvas.drawRect(detailX, detailY, detailWidth, detailHeight, '#ffffff');
    canvas.drawRectOutline(detailX, detailY, detailWidth, detailHeight, CONSTANTS.TABLE_BORDER);

    if (this.selectedGladiatorIndex >= this.gameState!.recruitableGladiatorIds.length) {
      canvas.drawText('Valitse gladiaattori', detailX + detailWidth / 2, detailY + detailHeight / 2,
        CONSTANTS.MAIN_FONT, '#666666', 'center');
      return;
    }

    const gladiatorId = this.gameState!.recruitableGladiatorIds[this.selectedGladiatorIndex];
    const gladiator = this.gameState!.gladiators.get(gladiatorId);
    if (!gladiator) return;

    // Large portrait
    const portrait = this.context.assets.gladiators[gladiator.race.id];
    if (portrait) {
      canvas.drawImage(portrait, detailX + 20, detailY + 20, 80, 80);
    }

    // Name and race
    canvas.drawText(gladiator.name, detailX + 115, detailY + 25, CONSTANTS.GLADIATOR_NAME_FONT, CONSTANTS.GLADIATOR_TITLE);
    canvas.drawText(gladiator.race.name, detailX + 115, detailY + 50, CONSTANTS.BIG_FONT, '#000000');
    canvas.drawText(`Ikä: ${gladiator.age} vuotta`, detailX + 115, detailY + 75, CONSTANTS.MAIN_FONT, '#666666');

    // Stats columns
    const statsY = detailY + 115;
    const col1X = detailX + 25;
    const col2X = detailX + 140;
    const col3X = detailX + 260;
    const lineHeight = 20;

    // Column 1: Base stats
    canvas.drawText('Voima:', col1X, statsY, CONSTANTS.GLADIATOR_STAT_TITLE_FONT, '#000000');
    canvas.drawText(String(gladiator.str), col1X + 60, statsY, CONSTANTS.MAIN_FONT, '#000066');

    canvas.drawText('Nopeus:', col1X, statsY + lineHeight, CONSTANTS.GLADIATOR_STAT_TITLE_FONT, '#000000');
    canvas.drawText(String(gladiator.speed), col1X + 60, statsY + lineHeight, CONSTANTS.MAIN_FONT, '#000066');

    canvas.drawText('Kesto:', col1X, statsY + lineHeight * 2, CONSTANTS.GLADIATOR_STAT_TITLE_FONT, '#000000');
    canvas.drawText(String(gladiator.maxHp), col1X + 60, statsY + lineHeight * 2, CONSTANTS.MAIN_FONT, '#000066');

    canvas.drawText('Mana:', col1X, statsY + lineHeight * 3, CONSTANTS.GLADIATOR_STAT_TITLE_FONT, '#000000');
    canvas.drawText(String(gladiator.maxMana), col1X + 60, statsY + lineHeight * 3, CONSTANTS.MAIN_FONT, '#000066');

    // Column 2: Melee skills
    canvas.drawText('Keihäs:', col2X, statsY, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(`${gladiator.skills.meleeAtk[1]}/${gladiator.skills.meleeDef[1]}`, col2X + 50, statsY, CONSTANTS.MSG_FONT, '#000066');

    canvas.drawText('Miekka:', col2X, statsY + lineHeight, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(`${gladiator.skills.meleeAtk[2]}/${gladiator.skills.meleeDef[2]}`, col2X + 50, statsY + lineHeight, CONSTANTS.MSG_FONT, '#000066');

    canvas.drawText('Kirves:', col2X, statsY + lineHeight * 2, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(`${gladiator.skills.meleeAtk[3]}/${gladiator.skills.meleeDef[3]}`, col2X + 50, statsY + lineHeight * 2, CONSTANTS.MSG_FONT, '#000066');

    canvas.drawText('Nuija:', col2X, statsY + lineHeight * 3, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(`${gladiator.skills.meleeAtk[4]}/${gladiator.skills.meleeDef[4]}`, col2X + 50, statsY + lineHeight * 3, CONSTANTS.MSG_FONT, '#000066');

    // Column 3: Ranged and abilities
    canvas.drawText('Jousi:', col3X, statsY, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(String(gladiator.skills.rangedAtk[0]), col3X + 55, statsY, CONSTANTS.MSG_FONT, '#000066');

    canvas.drawText('Varsijousi:', col3X, statsY + lineHeight, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(String(gladiator.skills.rangedAtk[1]), col3X + 55, statsY + lineHeight, CONSTANTS.MSG_FONT, '#000066');

    canvas.drawText('Väistö:', col3X, statsY + lineHeight * 2, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(String(gladiator.skills.abilities[0]), col3X + 55, statsY + lineHeight * 2, CONSTANTS.MSG_FONT, '#000066');

    canvas.drawText('Vastustus:', col3X, statsY + lineHeight * 3, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(String(gladiator.skills.abilities[1]), col3X + 55, statsY + lineHeight * 3, CONSTANTS.MSG_FONT, '#000066');

    // Salary
    canvas.drawText(`Palkka: ${gladiator.salary} ${GAME_CONSTANTS.MONEY_SUFFIX}/kk`, col1X, statsY + lineHeight * 5, CONSTANTS.MAIN_FONT, '#660000');
  }

  private renderBidControls(canvas: Canvas, team: any): void {
    const controlX = 340;
    const controlY = 375;
    const controlWidth = 440;
    const controlHeight = 85;

    canvas.drawRect(controlX, controlY, controlWidth, controlHeight, CONSTANTS.BUTTON_AREA_BACKGROUND);

    if (this.recruitmentDone) return;

    // Check if it's this player's turn
    const isPlayerTurn = this.isCurrentPlayerTurn();

    if (this.selectedGladiatorIndex >= this.gameState!.recruitableGladiatorIds.length) return;

    const gladiatorId = this.gameState!.recruitableGladiatorIds[this.selectedGladiatorIndex];
    const gladiator = this.gameState!.gladiators.get(gladiatorId);
    if (!gladiator) return;

    // Check if team can recruit this gladiator
    const canRecruit = !hasRace(team, gladiator.race, this.gameState!.gladiators) &&
                       team.gladiatorIds.length < GAME_CONSTANTS.MAX_GLADIATORS_IN_TEAM;

    // Minimum bid
    const minBid = gladiator.offerPrice + 10;

    // Bid input label
    canvas.drawText('Tarjous:', controlX + 20, controlY + 20, CONSTANTS.BIG_FONT, '#ffffff');

    // Bid input field
    const inputX = controlX + 110;
    const inputY = controlY + 12;
    const inputWidth = 120;
    const inputHeight = 32;

    // Input background - highlight if focused
    const inputBgColor = this.bidInputFocused ? '#ffffff' : '#dddddd';
    const inputBorderColor = this.bidInputFocused ? '#0066ff' : '#888888';
    canvas.drawRect(inputX, inputY, inputWidth, inputHeight, inputBgColor);
    canvas.drawRectOutline(inputX, inputY, inputWidth, inputHeight, inputBorderColor);
    if (this.bidInputFocused) {
      // Draw a thicker border for focus
      canvas.drawRectOutline(inputX - 1, inputY - 1, inputWidth + 2, inputHeight + 2, inputBorderColor);
    }

    // Input text with cursor
    const displayText = this.bidInputFocused ? this.bidInputText + '|' : this.bidInputText;
    canvas.drawText(displayText, inputX + 10, inputY + 8, CONSTANTS.BIG_FONT, '#000000');

    // Currency suffix
    canvas.drawText('kr', inputX + inputWidth + 10, inputY + 8, CONSTANTS.BIG_FONT, '#ffffff');

    // Click region for input focus
    canvas.addClickableRegion({
      id: 'bid-input',
      x: inputX,
      y: inputY,
      width: inputWidth,
      height: inputHeight,
      onClick: () => {
        this.bidInputFocused = true;
        this.context.input?.focusMobileInput(this.bidInputText, 'numeric');
      },
    });

    // Minimum bid hint
    canvas.drawText(`min: ${minBid}`, inputX + inputWidth + 50, inputY + 10, CONSTANTS.MSG_FONT, '#cccccc');

    // Turn indicator
    if (!isPlayerTurn) {
      canvas.drawText('Odotetaan muita joukkueita...', controlX + 20, controlY + controlHeight - 15,
        CONSTANTS.MSG_FONT, '#ffff66');
    }

    // Place bid button
    const btnY = controlY + 50;
    const canBid = isPlayerTurn && canRecruit && this.bidAmount >= minBid && this.bidAmount <= team.money;
    if (canBid) {
      canvas.drawButton('place-bid', 'Tee tarjous', controlX + 20, btnY, 140, 28,
        CONSTANTS.MAIN_BUTTON_FONT, '#ffffff', '#004400', () => {
          this.placeBid(gladiator);
        });
    } else {
      // Disabled button
      canvas.drawRect(controlX + 20, btnY, 140, 28, '#444444');
      canvas.drawText('Tee tarjous', controlX + 90, btnY + 7, CONSTANTS.MAIN_BUTTON_FONT, '#888888', 'center');
    }

    // Validation messages
    if (isPlayerTurn) {
      let warningMsg = '';
      let warningColor = '#ff6666';

      if (!canRecruit) {
        if (hasRace(team, gladiator.race, this.gameState!.gladiators)) {
          warningMsg = 'Joukkueella on jo tämän rodun gladiaattori!';
        } else if (team.gladiatorIds.length >= GAME_CONSTANTS.MAX_GLADIATORS_IN_TEAM) {
          warningMsg = 'Joukkue on täynnä!';
        }
      } else if (this.bidAmount < minBid) {
        warningMsg = `Tarjous liian pieni! Min: ${minBid} kr`;
      } else if (this.bidAmount > team.money) {
        warningMsg = `Ei tarpeeksi rahaa! Sinulla: ${team.money} kr`;
      }

      if (warningMsg) {
        canvas.drawText(warningMsg, controlX + 180, btnY + 7, CONSTANTS.MSG_FONT, warningColor);
      }
    }
  }

  private isCurrentPlayerTurn(): boolean {
    if (!this.gameState) return false;
    const team = getCurrentTeam(this.gameState);
    if (!team) return false;

    const currentTurnTeamId = this.gameState.recruitOrder[this.gameState.currentRecruitTeamIdx];
    return currentTurnTeamId === team.id;
  }

  private renderBackButton(canvas: Canvas): void {
    canvas.drawButton(
      'back',
      'Takaisin',
      20,
      CONSTANTS.CANVAS_HEIGHT - 50,
      100,
      35,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      CONSTANTS.BUTTON_AREA_BACKGROUND,
      () => {
        this.context.onScreenChange(ScreenState.MAIN_MENU);
      }
    );

    if (this.recruitmentDone) return;

    // Skip turn button - only enabled on player's turn
    const isPlayerTurn = this.isCurrentPlayerTurn();
    if (isPlayerTurn) {
      canvas.drawButton(
        'skip-recruit',
        'Ohita kierros',
        CONSTANTS.CANVAS_WIDTH - 140,
        CONSTANTS.CANVAS_HEIGHT - 50,
        120,
        35,
        CONSTANTS.MAIN_BUTTON_FONT,
        '#ffffff',
        '#666600',
        () => {
          this.skipRecruitTurn();
        }
      );
    } else {
      // Disabled skip button
      canvas.drawRect(CONSTANTS.CANVAS_WIDTH - 140, CONSTANTS.CANVAS_HEIGHT - 50, 120, 35, '#444444');
      canvas.drawText('Ohita kierros', CONSTANTS.CANVAS_WIDTH - 80, CONSTANTS.CANVAS_HEIGHT - 38,
        CONSTANTS.MAIN_BUTTON_FONT, '#888888', 'center');
    }
  }

  private placeBid(gladiator: GladiatorData): void {
    const team = getCurrentTeam(this.gameState!);
    if (!team) return;

    // Refund previous bidder
    if (gladiator.offerTeamId) {
      const previousTeam = this.gameState!.teams.get(gladiator.offerTeamId);
      if (previousTeam) {
        previousTeam.money += gladiator.offerPrice;
      }
    }

    // Place new bid
    gladiator.offerPrice = this.bidAmount;
    gladiator.offerTeamId = team.id;
    team.money -= this.bidAmount;

    console.log(`Placed bid of ${this.bidAmount} on ${gladiator.name}`);

    // Advance to next recruit turn
    this.advanceRecruitTurn();
  }

  private skipRecruitTurn(): void {
    console.log('Skipping recruit turn');
    this.advanceRecruitTurn();
  }

  private advanceRecruitTurn(): void {
    // Advance to the next team in the recruit order
    this.gameState!.currentRecruitTeamIdx++;

    if (this.gameState!.currentRecruitTeamIdx >= this.gameState!.recruitOrder.length) {
      this.gameState!.currentRecruitTeamIdx = 0;
      this.gameState!.currentRecruitRound++;

      if (this.gameState!.currentRecruitRound > GAME_CONSTANTS.RECRUIT_TURNS) {
        // Recruitment complete - show results
        this.completeRecruitment();
        return;
      }
    }

    // Reset bid amount
    this.bidAmount = 50;

    // Process AI turns until it's a player's turn again
    this.processAITurns();
  }

  private processAITurns(): void {
    if (!this.gameState) return;

    // Keep processing while it's an AI team's turn
    while (!this.isCurrentPlayerTurn() && this.gameState.currentRecruitRound <= GAME_CONSTANTS.RECRUIT_TURNS) {
      const currentTurnTeamId = this.gameState.recruitOrder[this.gameState.currentRecruitTeamIdx];
      const team = this.gameState.teams.get(currentTurnTeamId);

      if (team && !this.gameState.playerTeamIds.includes(currentTurnTeamId)) {
        // AI team: make a simple decision
        this.processAIRecruitTurn(team);
      }

      // Advance to next team
      this.gameState.currentRecruitTeamIdx++;

      if (this.gameState.currentRecruitTeamIdx >= this.gameState.recruitOrder.length) {
        this.gameState.currentRecruitTeamIdx = 0;
        this.gameState.currentRecruitRound++;

        if (this.gameState.currentRecruitRound > GAME_CONSTANTS.RECRUIT_TURNS) {
          // Recruitment complete - show results
          this.completeRecruitment();
          return;
        }
      }
    }
  }

  private processAIRecruitTurn(team: any): void {
    if (!this.gameState) return;

    // Simple AI: randomly decide whether to bid on a gladiator
    if (Math.random() < 0.3) {
      // 30% chance to skip
      return;
    }

    // Find a gladiator the AI can recruit (doesn't already have the race)
    const validGladiators = this.gameState.recruitableGladiatorIds.filter(id => {
      const gladiator = this.gameState!.gladiators.get(id);
      if (!gladiator) return false;
      if (hasRace(team, gladiator.race, this.gameState!.gladiators)) return false;
      if (team.gladiatorIds.length >= GAME_CONSTANTS.MAX_GLADIATORS_IN_TEAM) return false;
      return true;
    });

    if (validGladiators.length === 0) return;

    // Pick a random gladiator to bid on
    const gladiatorId = validGladiators[Math.floor(Math.random() * validGladiators.length)];
    const gladiator = this.gameState.gladiators.get(gladiatorId);
    if (!gladiator) return;

    const minBid = gladiator.offerPrice + 10;
    const maxBid = Math.min(team.money, minBid + 100);

    if (maxBid < minBid) return; // Can't afford

    const bidAmount = minBid + Math.floor(Math.random() * (maxBid - minBid + 1));

    // Refund previous bidder
    if (gladiator.offerTeamId) {
      const previousTeam = this.gameState.teams.get(gladiator.offerTeamId);
      if (previousTeam) {
        previousTeam.money += gladiator.offerPrice;
      }
    }

    // Place bid
    gladiator.offerPrice = bidAmount;
    gladiator.offerTeamId = team.id;
    team.money -= bidAmount;

    console.log(`AI ${team.name} bid ${bidAmount} on ${gladiator.name}`);
  }

  private completeRecruitment(): void {
    // Award gladiators to winning bidders
    for (const gladiatorId of this.gameState!.recruitableGladiatorIds) {
      const gladiator = this.gameState!.gladiators.get(gladiatorId);
      if (gladiator && gladiator.offerTeamId) {
        const team = this.gameState!.teams.get(gladiator.offerTeamId);
        if (team) {
          gladiator.ownerId = team.id;
          team.gladiatorIds.push(gladiatorId);
          console.log(`${team.name} recruited ${gladiator.name}`);
        }
      }
    }

    // Keep recruitableGladiatorIds so the list stays visible with final bids
    this.recruitmentDone = true;
  }

  handleKeyDown(key: string): void {
    // After recruitment ends, only Escape goes back
    if (this.recruitmentDone) {
      if (key === 'Escape') {
        this.context.onScreenChange(ScreenState.MAIN_MENU);
      }
      return;
    }

    // Handle bid input when focused
    if (this.bidInputFocused) {
      // Handle mobile input events
      if (key.startsWith('__mobileInput:')) {
        const value = key.slice('__mobileInput:'.length);
        // Only allow digits for bid input
        this.bidInputText = value.replace(/[^0-9]/g, '').slice(0, 6);
        this.updateBidFromInput();
        return;
      }

      if (key === 'Escape') {
        this.bidInputFocused = false;
        this.context.input?.blurMobileInput();
      } else if (key === 'Enter') {
        this.bidInputFocused = false;
        this.context.input?.blurMobileInput();
        // Try to place bid if valid
        const gladiatorId = this.gameState!.recruitableGladiatorIds[this.selectedGladiatorIndex];
        const gladiator = this.gameState?.gladiators.get(gladiatorId);
        const team = getCurrentTeam(this.gameState!);
        if (gladiator && team && this.isCurrentPlayerTurn()) {
          const minBid = gladiator.offerPrice + 10;
          const canRecruit = !hasRace(team, gladiator.race, this.gameState!.gladiators) &&
                             team.gladiatorIds.length < GAME_CONSTANTS.MAX_GLADIATORS_IN_TEAM;
          if (canRecruit && this.bidAmount >= minBid && this.bidAmount <= team.money) {
            this.placeBid(gladiator);
          }
        }
      } else if (key === 'Backspace') {
        this.bidInputText = this.bidInputText.slice(0, -1);
        this.updateBidFromInput();
      } else {
        // Handle number keys (both regular and numpad)
        let digit: string | null = null;
        if (key >= '0' && key <= '9') {
          digit = key;
        } else if (key.startsWith('Numpad') && key.length === 7) {
          // Numpad0-Numpad9
          const numpadDigit = key.charAt(6);
          if (numpadDigit >= '0' && numpadDigit <= '9') {
            digit = numpadDigit;
          }
        }

        if (digit && this.bidInputText.length < 6) {
          this.bidInputText += digit;
          this.updateBidFromInput();
        }
      }
      return;
    }

    // Normal key handling when input not focused
    if (key === 'Escape') {
      this.context.onScreenChange(ScreenState.MAIN_MENU);
    } else if (key === 'ArrowUp') {
      this.selectedGladiatorIndex = Math.max(0, this.selectedGladiatorIndex - 1);
    } else if (key === 'ArrowDown') {
      this.selectedGladiatorIndex = Math.min(
        this.gameState!.recruitableGladiatorIds.length - 1,
        this.selectedGladiatorIndex + 1
      );
    }
  }

  private updateBidFromInput(): void {
    const parsed = parseInt(this.bidInputText, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      this.bidAmount = parsed;
    } else if (this.bidInputText === '') {
      this.bidAmount = 0;
    }
  }

  handleClick(x: number, y: number): void {
    // Unfocus input if clicking outside the input area
    const inputX = 450; // controlX + 110
    const inputY = 387; // controlY + 12
    const inputWidth = 120;
    const inputHeight = 32;

    if (x < inputX || x > inputX + inputWidth || y < inputY || y > inputY + inputHeight) {
      this.bidInputFocused = false;
      this.context.input?.blurMobileInput();
    }

    console.log(`RecruitScreen click at (${x}, ${y})`);
  }
}
